import { useNotification } from "@/composables/useNotification";
import { restClient } from "@/lib/raftApi";

const { showNotification } = useNotification();

const state = {
  userExposures: {},
  bioUserUid: null,
  bioSpaceNid: null,
  filterResponseData: null,
};

const mutations = {
  setUserExposures: (state, exposures) => (state.userExposures = exposures),
  setUserUid: (state, userUid) => (state.bioUserUid = userUid),
  setSpaceNid: (state, spaceNid) => (state.bioSpaceNid = spaceNid),
  setFilterResponseData: (state, data) => (state.filterResponseData = data),
};

const getters = {
  getBioMaterialsThatSupportPlasmidVectors: (state) => {
    const plasmidVectorTypes =
      state.filterResponseData?.material_type?.options || [];
    return plasmidVectorTypes
      .filter((bundle) => bundle.has_plasmid_vector_field === true)
      .map((bundle) => bundle.id);
  },
  getBiomaterialTypeOptions: (state) => {
    const options = state.filterResponseData?.material_type?.options || [];
    return options.map((bundle) => ({
      text: bundle.label,
      value: bundle.id,
    }));
  },
  getContainmentAllowedValues: (state) => {
    let allowedValues = state.filterResponseData?.biosafety_level?.options;
    // Convert object to array if necessary
    if (!Array.isArray(allowedValues) && typeof allowedValues === "object") {
      allowedValues = Object.keys(allowedValues).map((key) => ({
        value: key,
        text: allowedValues[key],
      }));
    }
    // Check if allowedValues is now an array
    if (Array.isArray(allowedValues)) {
      return allowedValues;
    }
    return [];
  },
  getContainmentLevels: (state, getters) => {
    const containmentLevels = {};
    const levels = getters.getContainmentAllowedValues;
    levels.forEach((level) => {
      containmentLevels[level.id] = level.label;
    });
    return containmentLevels;
  },
  getContainmentLevelOptions: (state, getters) => {
    const options = getters.getContainmentAllowedValues;
    return options.map((level) => ({
      value: level.id,
      text: level.label,
    }));
  },
  getUserExposures: (state) => state.userExposures,
  getProjectOptions: (state) => {
    const options = state.filterResponseData?.project?.options || [];
    return options
      .map((option) => ({
        text: option.label,
        value: option.id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  },
  getResearcherOptions: (state) => {
    const options = state.filterResponseData?.researcher?.options || [];
    return options
      .map((option) => ({
        text: option.label,
        value: option.id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  },
  getGroupOptions: (state) => {
    const options = state.filterResponseData?.group?.options || [];
    return options
      .map((option) => ({
        text: option.label,
        value: option.id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  },
  getSpaceOptions: (state) => {
    const options = state.filterResponseData?.space?.options || [];
    return options
      .map((option) => ({
        text: option.label,
        value: option.id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  },
  getRiskLevelsLabel: (state) =>
    state.filterResponseData?.biosafety_level?.label || "Risk Level",
};

const actions = {
  async fetchUserExposureEntityDisplayData({ state, commit, rootState }) {
    const options = {
      path: "entity-view-display/raft_metadata/bio_material_user_exposure",
      queries: {
        "display_id[]": "default",
        wrap_allowed_values: 1,
      },
    };
    try {
      const response = await restClient.get(options);
      const exposures = {};
      const allowedValues =
        response?.bio_material_user_exposure?.default
          ?.field_bio_material_exposure?.definition?.settings?.allowed_values;
      if (Array.isArray(allowedValues)) {
        allowedValues.forEach((value) => {
          exposures[value.value] = value.text;
        });
      }
      commit("setUserExposures", exposures);
    } catch (error) {
      const message = "Failed to fetch bio material user exposure: " + error;
      showNotification("error", message);
    }
  },
  async fetchBioSpaceListingOptions({ state, commit, rootState }) {
    let bioMaterialPath = "/biological/biological-material/filters?";

    if (!state.bioUserUid && !state.bioSpaceNid) {
      showNotification("error", "Missing user and space id");
    }
    if (state.bioUserUid) {
      bioMaterialPath += `user=${state.bioUserUid}`;
    }
    if (state.bioSpaceNid) {
      bioMaterialPath += `space=${state.bioSpaceNid}`;
    }
    const options = {
      path: bioMaterialPath,
      queries: {},
    };

    try {
      const resp = await restClient.get(options);
      commit("setFilterResponseData", resp);
    } catch (error) {
      const message =
        "Failed to fetch biological material options:" +
        (error.response?.data?.message || "unknown error");
      showNotification("error", message);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
